import type {
  ListingApplicationPayload,
  NonAtsListingApplicationPayload,
} from 'api/ats';
import {
  legacyGetUserEnvironmentForZustand,
  legacyUpdateUserPartiallyInZustandStore,
} from 'contexts/UserEnvironmentContext';
import { userDashboardApiCreateListingApplication } from 'modules/userDashboard/api/listingApplication/userDashboardApiCreateListingApplication';
import { userDashboardApiDeleteListingApplication } from 'modules/userDashboard/api/listingApplication/userDashboardApiDeleteListingApplication';
import {
  userDashboardApiFetchCurrentListingApplications,
  userDashboardApiGetFetchCurrentListingApplicationsSsrData,
} from 'modules/userDashboard/api/listingApplication/userDashboardApiFetchCurrentListingApplications';
import { currentSsrApiData } from 'rendering/state/ssrApiData';

import { userDashboardCreateThingsStore } from './utils/userDashboardCreateThingsStore';

type CreateApplicationResponse = {
  id: string;
  applicationId: string;
  type: string;
};

const { createThing, deleteThing, useThings, useIsThingsSaved } =
  userDashboardCreateThingsStore({
    fetch: userDashboardApiFetchCurrentListingApplications,
    getSsrApiData: userDashboardApiGetFetchCurrentListingApplicationsSsrData,
    create: async (
      payload: ListingApplicationPayload | NonAtsListingApplicationPayload,
    ): Promise<CreateApplicationResponse> => {
      const listingApplication =
        await userDashboardApiCreateListingApplication(payload);

      const { user } = legacyGetUserEnvironmentForZustand(currentSsrApiData());
      if (
        user &&
        'resumeS3FileId' in payload &&
        payload.resumeS3FileId &&
        (payload.replaceCurrentResume || !user.resumeId)
      ) {
        legacyUpdateUserPartiallyInZustandStore({
          resumeId: payload.resumeS3FileId,
        });
      }

      return {
        id: payload.id,
        type: payload.type,
        applicationId: listingApplication ? listingApplication.id : '',
      };
    },
    delete: async (listingId: string, applicationId: string) => {
      await userDashboardApiDeleteListingApplication(applicationId);

      return listingId;
    },
  });

// Actions

export async function userDashboardCreateListingApplication(
  payload: ListingApplicationPayload | NonAtsListingApplicationPayload,
) {
  await createThing(payload);
}

export async function userDashboardDeleteListingApplication(
  listingId: string,
  applicationId: string,
) {
  await deleteThing(listingId, applicationId);
}

// Hooks

export function useUserDashboardListingApplications() {
  const listingApplications = useThings();
  return { listingApplications };
}

export function useUserDashboardUserHasAppliedToListing(id: string) {
  const userHasAppliedToListing = useIsThingsSaved(id);
  return { userHasAppliedToListing };
}
